/**
 * Last Minute Club Modal — shows a rolling 3-day window of available slots
 * across all 3 clinics, filtered by treatment duration (15 / 30 / 45 min).
 *
 * Backed by:
 *   GET /api/last-minute/slots?duration=15|30|45
 *
 * Clicking a slot fires `onPickSlot({location_id, date, time, duration})`
 * which the parent uses to open InlineBookingFlow with the slot prefilled.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Clock, MapPin, Calendar, Loader2, X, Zap, ChevronRight, Users, MessageCircle, Mail, LayoutGrid } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { useLanguage } from "@/context/LanguageContext";
import { openContactModal } from "@/components/ContactModal";

const DURATIONS = [
  { value: 15, key: "quick" },
  { value: 30, key: "mid" },
  { value: 45, key: "long" },
];

function durationLabel(d, isFr) {
  // Short label shown on the filter chip (1 line).
  if (d === 15) return isFr ? "Suivi · Consult virtuel" : "Follow-up · Virtual consult";
  if (d === 30) return isFr ? "Botox · Skin Boosters · Lèvres" : "Botox · Skin Boosters · Lip";
  return isFr ? "Sculptra · Microneedling" : "Sculptra · Microneedling";
}

function durationDetail(d, isFr) {
  // Longer detail shown under the filter when selected, so clients can
  // narrow down which length matches the treatment they want.
  if (d === 15) {
    return isFr
      ? "Idéal pour : retouches Botox, lip flip, lignes de lapin, suivis rapides, consultations virtuelles."
      : "Best for: Botox top-ups, lip flip, bunny lines, quick follow-ups, virtual consults.";
  }
  if (d === 30) {
    return isFr
      ? "Idéal pour : Botox complet, Skin Boosters, retouches de lèvres, retouches PRP."
      : "Best for: full Botox, Skin Boosters, lip filler touch-ups, PRP touch-ups.";
  }
  return isFr
    ? "Idéal pour : Botox + filler combo, Sculptra, microneedling médical, soins combinés."
    : "Best for: Botox + filler combos, Sculptra, medical microneedling, combo treatments.";
}

// Oct 16 2026 · Karine ask — LMC must say what each provider offers
// (mirrors the booking-area ProviderChooserModal specs) so clients know
// whether a slot is right for them before they tap it.
const LMC_OFFERS = {
  karine: {
    en: "Offers: Botox · Dermal Fillers · PRP · Sculptra · all treatments — Ottawa, Kanata & Carleton Place",
    fr: "Offre : Botox · Fillers · PRP · Sculptra · tous les soins — Ottawa, Kanata et Carleton Place",
  },
  linda: {
    en: "Offers: Botox · Dermal Fillers · Sculptra · consultations — Ottawa only (no laser or facials)",
    fr: "Offre : Botox · Fillers · Sculptra · consultations — Ottawa seulement (pas de laser ni de soins du visage)",
  },
  tanya: {
    en: "Offers: Laser Hair Removal · HydraFacial · RF Microneedling · Laser Genesis · Éminence facials — Ottawa only (no injectables)",
    fr: "Offre : épilation laser · HydraFacial · RF Microneedling · Laser Genesis · soins Éminence — Ottawa seulement (pas d'injectables)",
  },
};
const LmcOffers = ({ who, isFr, tone }) => (
  <div className={`${tone} text-[10px] leading-snug pl-4 -mt-0.5 mb-1.5 opacity-90`} data-testid={`lmc-offers-${who}`}>
    {LMC_OFFERS[who][isFr ? "fr" : "en"]}
  </div>
);

export default function LastMinuteClubModal({ open, onClose, onPickSlot }) {
  const { language } = useLanguage();
  const isFr = language === "fr";

  const [duration, setDuration] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Iter 458 · Feb 2026 · Karine ask — provider filter so clients can
  // hide the "other" provider with one tap. 'both' | 'karine' | 'tanya'.
  const [providerFilter, setProviderFilter] = useState("both");
  // Real "social proof" counter — bookings completed in the last hour.
  const [recentBookings, setRecentBookings] = useState(0);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    fetch(`${process.env.REACT_APP_BACKEND_URL}/api/last-minute/recent-activity`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => { if (!cancelled && d) setRecentBookings(d.recent_bookings || 0); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    let retryTimer = null;
    const fetchSlots = () => {
      setLoading(true);
      setError(null);
      fetch(`${process.env.REACT_APP_BACKEND_URL}/api/last-minute/slots?duration=${duration}`)
        .then((r) => (r.ok ? r.json() : Promise.reject(new Error("fetch failed"))))
        .then((d) => {
          if (cancelled) return;
          setData(d);
          // If the backend just kicked off a cold-cache refresh, poll again
          // in 6s so the user sees fresh slots as soon as they're ready
          // (avoids the post-redeploy empty state).
          if (d?.refreshing && !cancelled) {
            retryTimer = setTimeout(fetchSlots, 6000);
          }
        })
        .catch((e) => { if (!cancelled) setError(e.message || "Could not load"); })
        .finally(() => { if (!cancelled) setLoading(false); });
    };
    fetchSlots();
    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
    };
  }, [open, duration]);

  const totalSlots = data?.total || 0;

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent
        className="sm:max-w-3xl md:max-w-4xl lg:max-w-5xl bg-gradient-to-br from-zinc-950 via-zinc-900 to-amber-950/20 border-amber-500/30 text-zinc-100 max-h-[92vh] overflow-y-auto p-0 z-[210] pb-24 sm:pb-6"
        data-testid="last-minute-modal"
      >
        {/* Iter 293s · Feb 2026 · Karine bug — the "All booked solid" screen
            hides the modal's own close button under the portal's bottom nav,
            so the user gets stuck.
            Aug 25 2026 · re-fix — Karine reported the sticky × failed on
            iOS Safari because `sticky top-2 + float:right` doesn't stick.
            Switched to `absolute top-3 right-3` inside DialogContent
            (which is `position: fixed` per Radix) so the close pill is
            always pinned to the top-right corner regardless of scroll.
            Also bumped the modal's z-index to 210 so it clears the client
            portal's bottom nav (z-50) and MobileKarineWidget (z-200)
            which were stealing taps on the last row of slot buttons. */}
        <button
          type="button"
          onClick={() => onClose?.()}
          aria-label={isFr ? "Fermer" : "Close"}
          data-testid="lmc-sticky-close"
          className="absolute top-3 right-3 z-[220] w-11 h-11 rounded-full bg-black/80 backdrop-blur border border-amber-500/50 text-amber-100 flex items-center justify-center shadow-lg hover:bg-amber-500/25 hover:border-amber-400 transition"
        >
          <X className="w-5 h-5" />
        </button>

        <DialogHeader className="px-6 pt-6 pb-4 border-b border-amber-500/20">
          <div className="flex items-start justify-between gap-3">
            <div>
              <DialogTitle className="text-amber-200 font-serif text-2xl tracking-wide flex items-center gap-2">
                <Zap className="w-5 h-5 text-amber-400" />
                {isFr ? "Club Dernière Minute" : "Last Minute Club"}
              </DialogTitle>
              <DialogDescription className="text-zinc-400 mt-1.5 text-sm">
                {isFr
                  ? "Accès exclusif aux ouvertures des 72 prochaines heures — saisissez-en une avant qu'elle disparaisse."
                  : "Exclusive access to openings in the next 72 hours — grab one before it disappears."}
              </DialogDescription>
              <Link
                to="/last-minute-club"
                onClick={() => onClose?.()}
                data-testid="lmc-view-all-page"
                className="inline-flex items-center gap-1.5 mt-3 px-3 py-1.5 rounded-full border border-amber-500/40 text-amber-200 hover:bg-amber-500/10 hover:border-amber-400 text-[11px] font-semibold tracking-wide transition group"
              >
                <LayoutGrid className="w-3 h-3" />
                {isFr ? "Voir toutes les ouvertures" : "View all openings"}
                <ChevronRight className="w-3 h-3 group-hover:translate-x-0.5 transition" />
              </Link>
            </div>
          </div>
        </DialogHeader>

        {/* Activity ticker — only when there's been real recent activity */}
        {recentBookings > 0 && (
          <div
            className="mx-6 mt-3 px-3 py-1.5 rounded-md bg-emerald-500/8 border border-emerald-500/25 flex items-center gap-2 text-[11px] text-emerald-300"
            data-testid="lmc-activity-ticker"
          >
            <Users className="w-3 h-3" />
            {isFr
              ? `${recentBookings} plage${recentBookings > 1 ? "s" : ""} réservée${recentBookings > 1 ? "s" : ""} dans la dernière heure`
              : `${recentBookings} spot${recentBookings > 1 ? "s" : ""} booked in the last hour`}
          </div>
        )}

        {/* Duration filter */}
        <div className="px-6 pt-4">
          <p className="text-[10px] text-zinc-500 uppercase tracking-widest mb-2">
            {isFr ? "Durée du soin" : "Treatment length"}
          </p>
          <div className="flex gap-2 flex-wrap" role="tablist">
            {DURATIONS.map((d) => (
              <button
                key={d.value}
                role="tab"
                aria-selected={duration === d.value}
                onClick={() => setDuration(d.value)}
                data-testid={`lmc-duration-${d.value}`}
                className={`px-3 py-2 rounded-lg border text-xs transition-all ${
                  duration === d.value
                    ? "bg-amber-500/20 border-amber-500/60 text-amber-200"
                    : "bg-zinc-900/40 border-zinc-800 text-zinc-400 hover:text-zinc-200 hover:border-zinc-700"
                }`}
              >
                <div className="font-semibold">{d.value} min</div>
                <div className="text-[10px] mt-0.5 max-w-[160px] truncate">
                  {durationLabel(d.value, isFr)}
                </div>
              </button>
            ))}
          </div>
          <p
            className="text-[11px] text-amber-300/80 mt-2 leading-relaxed"
            data-testid={`lmc-duration-detail-${duration}`}
          >
            {durationDetail(duration, isFr)}
          </p>
        </div>

        <div className="px-6 py-5">
          {loading ? (
            <div className="flex items-center justify-center py-16 text-zinc-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2 text-amber-400" />
              {isFr ? "Recherche des ouvertures…" : "Finding last-minute openings…"}
            </div>
          ) : error ? (
            <div className="py-12 text-center text-rose-300 text-sm">
              {isFr ? "Impossible de charger pour le moment." : "Couldn't load right now."}
            </div>
          ) : totalSlots === 0 ? (
            data?.refreshing ? (
              <div className="py-10 text-center" data-testid="lmc-warming">
                <div className="w-10 h-10 border-2 border-amber-500/30 border-t-amber-500 rounded-full animate-spin mx-auto mb-3" />
                <p className="text-amber-200 font-medium mb-1">
                  {isFr ? "Chargement des disponibilités..." : "Loading live availability..."}
                </p>
                <p className="text-zinc-500 text-xs max-w-sm mx-auto leading-relaxed">
                  {isFr
                    ? "Nous récupérons les créneaux directement depuis Square. Un instant..."
                    : "We're pulling slots directly from Square. This takes a few seconds — refreshing automatically..."}
                </p>
              </div>
            ) : (
              <div className="py-10 text-center" data-testid="lmc-empty">
                <Calendar className="w-10 h-10 text-zinc-700 mx-auto mb-3" />
                <p className="text-zinc-300 font-medium mb-1">
                  {isFr ? "Aucune ouverture pour le moment." : "All booked solid right now."}
                </p>
                <p className="text-zinc-500 text-xs max-w-sm mx-auto leading-relaxed">
                  {isFr
                    ? "Essayez une autre durée ci-dessus, ou rejoignez la liste d'attente pour être prévenu(e) d'une annulation."
                    : "Try a different duration above, or join the waitlist to be the first notified of a cancellation."}
                </p>
              </div>
            )
          ) : (
            <div className="space-y-5" data-testid="lmc-slots">
              {(() => {
                // Iter 458 · Feb 2026 · Karine ask — hoisted provider counts
                // (used by both the summary header + the filter chip strip
                // below) so we don't traverse `by_day` twice.
                // Iter 461 · Feb 2026 · production crash fix — `x || []`
                // falls through when `x` is a truthy non-array (e.g. `{}`
                // from a cache glitch). Use Array.isArray guards.
                const asArr = (v) => (Array.isArray(v) ? v : []);
                let karineCount = 0;
                let tanyaCount = 0;
                asArr(data?.by_day).forEach((day) => {
                  asArr(day?.locations).forEach((loc) => {
                    asArr(loc?.slots).forEach((slot) => {
                      if (slot?.provider === "tanya") tanyaCount += 1;
                      else karineCount += 1;
                    });
                  });
                });
                const totalActive =
                  providerFilter === "karine" ? karineCount
                  : providerFilter === "tanya" ? tanyaCount
                  : karineCount + tanyaCount;
                let headerText;
                if (tanyaCount === 0) {
                  headerText = isFr
                    ? `Karine a libéré ${karineCount} ouverture${karineCount > 1 ? "s" : ""} sur les 3 prochains jours`
                    : `Karine opened ${karineCount} availability slot${karineCount > 1 ? "s" : ""} in the next 3 days`;
                } else if (karineCount === 0) {
                  headerText = isFr
                    ? `Tanya (esthéticienne médicale) a libéré ${tanyaCount} ouverture${tanyaCount > 1 ? "s" : ""} sur les 3 prochains jours`
                    : `Tanya (Medical Aesthetician) opened ${tanyaCount} availability slot${tanyaCount > 1 ? "s" : ""} in the next 3 days`;
                } else if (providerFilter === "karine") {
                  headerText = isFr
                    ? `Filtré : ${karineCount} avec Karine sur les 3 prochains jours`
                    : `Filtered: ${karineCount} with Karine in the next 3 days`;
                } else if (providerFilter === "tanya") {
                  headerText = isFr
                    ? `Filtré : ${tanyaCount} avec Tanya (esthéticienne médicale) sur les 3 prochains jours`
                    : `Filtered: ${tanyaCount} with Tanya (Medical Aesthetician) in the next 3 days`;
                } else {
                  headerText = isFr
                    ? `${karineCount} avec Karine · ${tanyaCount} avec Tanya (esthéticienne médicale) sur les 3 prochains jours`
                    : `${karineCount} with Karine · ${tanyaCount} with Tanya (Medical Aesthetician) in the next 3 days`;
                }
                // Only show the filter chip strip when BOTH providers have
                // slots — no point offering the toggle when there's only
                // one provider on screen.
                const showFilter = karineCount > 0 && tanyaCount > 0;
                return (
                  <>
                    <p className="text-xs text-amber-300" data-testid="lmc-header-summary">
                      {headerText}
                    </p>
                    {showFilter && (
                      <div
                        className="flex items-center gap-1.5 -mt-2"
                        role="tablist"
                        aria-label={isFr ? "Filtrer par fournisseur" : "Filter by provider"}
                        data-testid="lmc-provider-filter"
                      >
                        {[
                          { key: "both", label: isFr ? "Les deux" : "Both", count: karineCount + tanyaCount, tone: "amber" },
                          { key: "karine", label: isFr ? "Karine seulement" : "Karine only", count: karineCount, tone: "amber" },
                          { key: "tanya", label: isFr ? "Tanya seulement" : "Tanya only", count: tanyaCount, tone: "sky" },
                        ].map((chip) => {
                          const active = providerFilter === chip.key;
                          const isSky = chip.tone === "sky";
                          return (
                            <button
                              key={chip.key}
                              type="button"
                              role="tab"
                              aria-selected={active}
                              onClick={() => setProviderFilter(chip.key)}
                              className={`flex-1 sm:flex-none inline-flex items-center justify-center gap-1 px-2.5 py-1.5 rounded-full text-[11px] font-semibold border transition-all ${
                                active
                                  ? isSky
                                    ? "bg-sky-500/20 border-sky-400 text-sky-100 shadow-sm shadow-sky-500/20"
                                    : "bg-amber-500/20 border-amber-400 text-amber-100 shadow-sm shadow-amber-500/20"
                                  : "bg-zinc-800/40 border-zinc-700 text-zinc-400 hover:text-zinc-200 hover:border-zinc-500"
                              }`}
                              data-testid={`lmc-filter-${chip.key}`}
                            >
                              {chip.label}
                              <span className={`text-[9px] font-bold ${active ? "opacity-80" : "opacity-60"}`}>
                                · {chip.count}
                              </span>
                            </button>
                          );
                        })}
                      </div>
                    )}
                    {totalActive === 0 && (
                      <div
                        className="rounded-lg border border-zinc-700 bg-zinc-800/30 px-4 py-3 text-center text-xs text-zinc-400"
                        data-testid="lmc-filter-empty"
                      >
                        {providerFilter === "karine"
                          ? (isFr ? "Aucune plage Karine sur les 3 prochains jours." : "No Karine openings in the next 3 days.")
                          : (isFr ? "Aucune plage Tanya sur les 3 prochains jours." : "No Tanya openings in the next 3 days.")}
                        <button
                          type="button"
                          onClick={() => setProviderFilter("both")}
                          className="ml-2 text-amber-300 hover:text-amber-200 underline underline-offset-2"
                          data-testid="lmc-filter-reset"
                        >
                          {isFr ? "Afficher les deux" : "Show both"}
                        </button>
                      </div>
                    )}
                  </>
                );
              })()}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-3">
              {(Array.isArray(data?.by_day) ? data.by_day : [])
                .filter((day) => {
                  // Iter 458 · providerFilter — hide entire day cards
                  // that have zero visible slots after filtering so we
                  // don't render empty containers.
                  // Iter 461 · defensive Array.isArray on nested paths.
                  if (providerFilter === "both") return true;
                  const locs = Array.isArray(day?.locations) ? day.locations : [];
                  return locs.some((loc) => {
                    const slots = Array.isArray(loc?.slots) ? loc.slots : [];
                    return slots.some((s) => {
                      const p = s?.provider === "tanya" ? "tanya"
                              : s?.provider === "linda" ? "linda"
                              : "karine";
                      return p === providerFilter;
                    });
                  });
                })
                .map((day) => (
                <div key={day.date} className="rounded-xl border border-zinc-800 bg-zinc-900/40 overflow-hidden">
                  <div className="px-4 py-2.5 border-b border-zinc-800 bg-zinc-900/60 flex items-center justify-between">
                    <div>
                      <p className="text-amber-200 text-sm font-semibold">{day.weekday}</p>
                      <p className="text-[11px] text-zinc-500">{day.month_day}</p>
                    </div>
                  </div>
                  <div className="p-3 space-y-3">
                    {(Array.isArray(day.locations) ? day.locations : []).map((loc) => {
                      // Iter 457 · Karine ask — group slots by provider so
                      // Karine's openings render separately from Tanya's
                      // (Medical Aesthetician, Ottawa only). Preserves
                      // the existing sort order within each group.
                      // Iter 458 · providerFilter ('both'|'karine'|'tanya')
                      // hides the "other" provider's slots one-tap style.
                      // Iter 461 · Array.isArray guard so a malformed
                      // slots value never crashes the modal.
                      // Feb 2026 · Karine ask — added Linda bucket so
                      // Ottawa Thursday NP slots stop mis-labeling as
                      // Karine. Linda gets a teal palette (NP).
                      const groups = { karine: [], tanya: [], linda: [] };
                      (Array.isArray(loc.slots) ? loc.slots : []).forEach((slot) => {
                        const raw = (slot?.provider || "").toLowerCase();
                        const p = raw === "tanya" ? "tanya"
                                : raw === "linda" ? "linda"
                                : "karine";
                        if (providerFilter !== "both" && providerFilter !== p) return;
                        groups[p].push(slot);
                      });
                      const visibleCount = groups.karine.length + groups.tanya.length + groups.linda.length;
                      if (visibleCount === 0) return null;
                      const renderSlotButtons = (slots) => (
                        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-1.5">
                          {slots.map((slot) => {
                            const _p = (slot?.provider || "").toLowerCase();
                            const _cls = _p === "tanya"
                              ? "border-sky-500/40 bg-sky-500/5 hover:bg-sky-500/15 hover:border-sky-400 text-sky-200"
                              : _p === "linda"
                              ? "border-teal-500/40 bg-teal-500/5 hover:bg-teal-500/15 hover:border-teal-400 text-teal-200"
                              : "border-amber-500/30 bg-amber-500/5 hover:bg-amber-500/15 hover:border-amber-400 text-amber-200";
                            return (
                              <button
                                key={`${loc.location_id}-${day.date}-${slot.start_time}-${slot.provider || "karine"}`}
                                onClick={() => onPickSlot?.({
                                  location_id: loc.location_id,
                                  location_name: loc.name,
                                  date: day.date,
                                  weekday: day.weekday,
                                  month_day: day.month_day,
                                  start_time: slot.start_time,
                                  duration_minutes: duration,
                                  provider: slot.provider || "karine",
                                })}
                                className={`px-2.5 py-2 rounded-lg border text-xs font-medium transition-all flex items-center justify-center gap-1 group ${_cls}`}
                                data-testid={`lmc-slot-${loc.location_id}-${day.date}-${slot.start_time}-${slot.provider || "karine"}`}
                              >
                                <Clock className="w-3 h-3 opacity-60 group-hover:opacity-100" />
                                {slot.label}
                                <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition" />
                              </button>
                            );
                          })}
                        </div>
                      );
                      return (
                        <div key={loc.location_id}>
                          <div className="flex items-center gap-1.5 mb-1.5">
                            <MapPin className="w-3 h-3 text-zinc-500" />
                            <span className="text-zinc-300 text-xs font-medium">{loc.name}</span>
                            <Badge className="bg-zinc-800/70 text-zinc-400 border-zinc-700 text-[9px] py-0">
                              {providerFilter === "both" ? (Array.isArray(loc.slots) ? loc.slots.length : 0) : visibleCount}
                            </Badge>
                          </div>
                          {groups.karine.length > 0 && (
                            <div className="mb-2" data-testid={`lmc-provider-group-karine-${loc.location_id}-${day.date}`}>
                              <div className="flex items-center gap-1.5 mb-1 pl-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-amber-400" />
                                <span className="text-amber-300 text-[10px] font-semibold uppercase tracking-wide">
                                  {isFr ? "Karine Lauzon, IA" : "Karine Lauzon, RN"}
                                </span>
                                <span className="text-zinc-500 text-[10px]">· {groups.karine.length}</span>
                              </div>
                              <LmcOffers who="karine" isFr={isFr} tone="text-amber-200/80" />
                              {renderSlotButtons(groups.karine)}
                            </div>
                          )}
                          {groups.tanya.length > 0 && (
                            <div data-testid={`lmc-provider-group-tanya-${loc.location_id}-${day.date}`}>
                              <div className="flex items-center gap-1.5 mb-1 pl-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-sky-400" />
                                <span className="text-sky-300 text-[10px] font-semibold uppercase tracking-wide">
                                  {isFr ? "Tanya Jackson · esthéticienne médicale" : "Tanya Jackson · Medical Aesthetician"}
                                </span>
                                <span className="text-zinc-500 text-[10px]">· {groups.tanya.length}</span>
                              </div>
                              <LmcOffers who="tanya" isFr={isFr} tone="text-sky-200/80" />
                              {renderSlotButtons(groups.tanya)}
                            </div>
                          )}
                          {groups.linda.length > 0 && (
                            <div data-testid={`lmc-provider-group-linda-${loc.location_id}-${day.date}`} className="mt-2">
                              <div className="flex items-center gap-1.5 mb-1 pl-1">
                                <span className="w-1.5 h-1.5 rounded-full bg-teal-400" />
                                <span className="text-teal-300 text-[10px] font-semibold uppercase tracking-wide">
                                  {isFr ? "Linda Lafrance · IPS" : "Linda Lafrance, NP · Nurse Practitioner"}
                                </span>
                                <span className="text-zinc-500 text-[10px]">· {groups.linda.length}</span>
                              </div>
                              <LmcOffers who="linda" isFr={isFr} tone="text-teal-200/80" />
                              {renderSlotButtons(groups.linda)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              </div>
            </div>
          )}
        </div>

        {/* "Can't find one? Message us" — always visible helper, especially
            useful in the empty state. Real human escape hatch with no spam. */}
        {!loading && !error && (
          <div className="mx-6 mb-4 rounded-xl border border-amber-500/25 bg-gradient-to-br from-zinc-900/80 to-amber-950/20 p-4" data-testid="lmc-help-cta">
            <p className="text-amber-200 text-sm font-medium leading-snug mb-1">
              {isFr
                ? "Vous ne trouvez pas ce qu'il vous faut ?"
                : "Need a last minute appointment and can't find one?"}
            </p>
            <p className="text-zinc-400 text-xs leading-relaxed mb-3">
              {isFr
                ? "Écrivez-nous — l'équipe Karine Lauzon RN fera son possible pour trouver une place pour vous."
                : "Message us — our Karine Lauzon RN team will do our best to find a spot for you."}
            </p>
            <div className="flex gap-2 flex-wrap">
              <a
                href="https://m.me/PreciseRNInjectionist" onClick={(e) => { e.preventDefault(); openContactModal({ source: "m-me-link" }); }}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-amber-600 hover:bg-amber-500 text-white text-xs font-semibold transition flex-1 justify-center"
                data-testid="lmc-help-messenger"
              >
                <MessageCircle className="w-3.5 h-3.5" />
                {isFr ? "Messenger" : "Messenger"}
              </a>
              <a
                href={`mailto:info@karinelauzon.com?subject=${encodeURIComponent(isFr ? "Demande de rendez-vous de dernière minute" : "Last minute appointment request")}&body=${encodeURIComponent(isFr ? "Bonjour, j'aimerais trouver un rendez-vous de dernière minute. Voici ce dont j'ai besoin :\n\nClinique préférée :\nSoin souhaité :\nDates ou heures qui me conviennent :\n\nMerci !" : "Hi Karine Lauzon RN team,\n\nI'd love to find a last-minute appointment. Here's what I'm hoping for:\n\nPreferred clinic:\nTreatment:\nDates / times that work for me:\n\nThank you!")}`}
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-amber-500/40 text-amber-200 text-xs font-semibold hover:bg-amber-500/10 transition flex-1 justify-center"
                data-testid="lmc-help-email"
              >
                <Mail className="w-3.5 h-3.5" />
                {isFr ? "Courriel" : "Email"}
              </a>
            </div>
          </div>
        )}

        <div className="px-6 pb-5 pt-2 border-t border-zinc-800/60 flex items-center justify-between gap-3" style={{ paddingBottom: "calc(1.25rem + env(safe-area-inset-bottom, 0px) + 72px)" }}>
          <p className="text-[10px] text-zinc-600">
            {isFr
              ? "Mise à jour automatique chaque 5 min"
              : "Auto-refreshes every 5 minutes"}
          </p>
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="text-zinc-400 hover:text-zinc-100 h-8"
            data-testid="lmc-close"
          >
            <X className="w-3.5 h-3.5 mr-1" /> {isFr ? "Fermer" : "Close"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
